import { Prize, PrizeStatus } from 'src/app/data/schema/prize';
import { Raffle } from 'src/app/data/schema/raffle';

export type PrizeSalesEndDirection = 'afterEnd' | 'beforeOrOnStart';

export interface IPrizeSalesEndValidation {
    valid: boolean;
    title?: string;
    message?: string;
    direction?: PrizeSalesEndDirection;
}

const MSG_AFTER_END = 'An early bird prize\'s sales end date must be before the raffle\'s sales end date.';
const MSG_BEFORE_START = 'An early bird prize\'s sales end date must be after the raffle\'s sales start date.';

export class PrizeValidationHelper {

    // ongoing validity of a saved prize against the raffle's current sales period (banner, prizesValid, post-save check)
    // a prize's sales end must be after the raffle's sales start and no later than the raffle's sales end
    static validateSalesEnd(prize: Prize, raffle: Raffle): IPrizeSalesEndValidation {
        const raffStart = new Date(raffle.saleStart).valueOf();
        const raffEnd = new Date(raffle.saleEnd).valueOf();
        const prizeEnd = new Date(prize.salesEnd).valueOf();

        if (prizeEnd <= raffStart) {
            return { valid: false, title: 'Invalid Sales End', message: MSG_BEFORE_START, direction: 'beforeOrOnStart' };
        } else if (prizeEnd > raffEnd) {
            return { valid: false, title: 'Invalid Sales End', message: MSG_AFTER_END, direction: 'afterEnd' };
        }

        return { valid: true };
    }

    // stricter check used only in the prize form while Early Bird is checked - equal to either raffle edge is blocked
    static validateEarlyBirdEntry(prize: Prize, raffle: Raffle): IPrizeSalesEndValidation {
        const raffStart = new Date(raffle.saleStart).valueOf();
        const raffEnd = new Date(raffle.saleEnd).valueOf();
        const prizeEnd = new Date(prize.salesEnd).valueOf();

        if (prizeEnd <= raffStart) {
            return { valid: false, title: 'Invalid Sales End', message: MSG_BEFORE_START, direction: 'beforeOrOnStart' };
        } else if (prizeEnd >= raffEnd) {
            return { valid: false, title: 'Invalid Sales End', message: MSG_AFTER_END, direction: 'afterEnd' };
        }

        return { valid: true };
    }

    // early bird is not stored, a prize is an early bird when its sales end differs from the raffle's
    // technically an early bird is before, but to handle a case where early bird ends up after.. we can
    // still keep the early bird indicator on
    static isEarlyBird(prize: Prize, raffle: Raffle): boolean {
        return new Date(raffle.saleEnd).valueOf() !== new Date(prize.salesEnd).valueOf();
    }

    // true only when a sales period change turns a previously-valid early bird into a standard prize
    // (its sales end now exactly matches the raffle's new sales end). Prizes already following the raffle
    // end, or already invalid before the change, are not early birds losing status - they're ignored.
    static becomesNoLongerEarlyBird(prize: Prize, before: Raffle, after: Raffle): boolean {
        if (prize.status !== PrizeStatus.pending) {
            return false;
        }

        const prizeEnd = new Date(prize.salesEnd).valueOf();
        if (prizeEnd === new Date(before.saleEnd).valueOf()) {
            return false;
        }

        if (PrizeValidationHelper.validateSalesEnd(prize, before).valid === false) {
            return false;
        }

        return prizeEnd === new Date(after.saleEnd).valueOf();
    }
}
